import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import SiteHeader from '../components/common/SiteHeader';
import Breadcrumb from '../components/common/Breadcrumb';
import BackButton from '../components/common/BackButton';
import SaveStatusIndicator from '../components/common/SaveStatusIndicator';
import InvitationInput from '../components/common/InvitationInput';
import VideoThumbnailCard from '../components/common/VideoThumbnailCard';
import AddVideoModal from '../components/common/AddVideoModal';
import ImportFromChannelSection from '../components/gallery/ImportFromChannelSection';
import galleryService from '../services/galleryService';

// Sortable item component for drag and drop
function SortableVideoItem({ video, onUpdate, onDelete, isSelectable, isSelected, onToggleSelect }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: video.id || video.url });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative">
      {!isSelectable && (
        <div
          {...attributes}
          {...listeners}
          className="absolute top-2 left-2 z-10 cursor-grab active:cursor-grabbing bg-white rounded-lg p-2 shadow-md hover:bg-gray-50 transition-colors"
          title="Drag to reorder"
        >
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
          </svg>
        </div>
      )}
      <VideoThumbnailCard
        video={video}
        onUpdate={onUpdate}
        onDelete={onDelete}
        isEditing={true}
        isSelectable={isSelectable}
        isSelected={isSelected}
        onToggleSelect={onToggleSelect}
      />
    </div>
  );
}

function ManageGalleryPage() {
  const { galleryId } = useParams();
  const navigate = useNavigate();
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [activeTab, setActiveTab] = useState('videos');
  const [showAddVideoModal, setShowAddVideoModal] = useState(false);
  const [isDangerZoneExpanded, setIsDangerZoneExpanded] = useState(false);

  // Selection state for bulk operations
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedVideoIds, setSelectedVideoIds] = useState(new Set());

  // Sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Form states
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [addNewVideosAtBeginning, setAddNewVideosAtBeginning] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [videoLinks, setVideoLinks] = useState([]);

  const loadGallery = useCallback(async () => {
    try {
      setLoading(true);
      const galleryData = await galleryService.getGalleryWithVideos(galleryId);
      setGallery(galleryData);
      setName(galleryData.gallery.name || '');
      setDescription(galleryData.gallery.description || '');
      setIsPrivate(galleryData.gallery.isPrivate || false);
      setAddNewVideosAtBeginning(galleryData.gallery.addNewVideosAtBeginning || false);
      setInvitations(galleryData.gallery.invitations || []);
      setVideoLinks(galleryData.videos.map(v => ({
        id: v.id,
        url: v.url,
        description: v.description,
        title: v.title,
        thumbnailUrl: v.thumbnailUrl,
        duration: v.duration,
        channelTitle: v.channelTitle
      })));
      setError(null);
    } catch (err) {
      setError('Failed to load gallery. It may not exist or you may not have access.');
    } finally {
      setLoading(false);
    }
  }, [galleryId]);

  useEffect(() => {
    loadGallery();
  }, [loadGallery]);

  // Auto-save handlers
  const handleNameBlur = async () => {
    setSaveStatus('saving');
    try {
      await galleryService.patchGallery(galleryId, { name: name.trim() });
      setSaveStatus('saved');
    } catch (err) {
      setSaveStatus('error');
      setError('Failed to save name');
    }
  };

  const handleDescriptionBlur = async () => {
    setSaveStatus('saving');
    try {
      await galleryService.patchGallery(galleryId, { description: description.trim() });
      setSaveStatus('saved');
    } catch (err) {
      setSaveStatus('error');
      setError('Failed to save description');
    }
  };

  const handlePrivacyChange = async (value) => {
    setIsPrivate(value);
    setSaveStatus('saving');
    try {
      await galleryService.patchGallery(galleryId, { isPrivate: value });
      setSaveStatus('saved');
    } catch (err) {
      setSaveStatus('error');
      setError('Failed to save privacy setting');
      setIsPrivate(!value); // Revert on error
    }
  };

  const handleAddNewVideosAtBeginningChange = async (value) => {
    setAddNewVideosAtBeginning(value);
    setSaveStatus('saving');
    try {
      await galleryService.patchGallery(galleryId, { addNewVideosAtBeginning: value });
      setSaveStatus('saved');
    } catch (err) {
      setSaveStatus('error');
      setError('Failed to save video setting');
      setAddNewVideosAtBeginning(!value); // Revert on error
    }
  };

  // Video handlers with immediate save
  const handleAddVideo = async (videoData) => {
    setSaveStatus('saving');
    try {
      const newVideo = await galleryService.addVideo(galleryId, videoData);
      if (addNewVideosAtBeginning) {
        setVideoLinks([newVideo, ...videoLinks]);
      } else {
        setVideoLinks([...videoLinks, newVideo]);
      }
      setSaveStatus('saved');
    } catch (err) {
      setSaveStatus('error');
      setError('Failed to add video');
    }
  };

  const handleUpdateVideo = async (videoId, videoData) => {
    setSaveStatus('saving');
    try {
      await galleryService.updateVideo(galleryId, videoId, {
        title: videoData.title,
        description: videoData.description
      });
      setVideoLinks(videoLinks.map(v =>
        v.id === videoId ? { ...v, ...videoData } : v
      ));
      setSaveStatus('saved');
    } catch (err) {
      setSaveStatus('error');
      setError('Failed to update video');
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('Are you sure you want to remove this video?')) {
      return;
    }
    setSaveStatus('saving');
    try {
      await galleryService.removeVideo(galleryId, videoId);
      setVideoLinks(videoLinks.filter(v => v.id !== videoId));
      setSaveStatus('saved');
    } catch (err) {
      setSaveStatus('error');
      setError('Failed to delete video');
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = videoLinks.findIndex((item) => (item.id || item.url) === active.id);
    const newIndex = videoLinks.findIndex((item) => (item.id || item.url) === over.id);
    const reordered = arrayMove(videoLinks, oldIndex, newIndex);

    setVideoLinks(reordered);
    setSaveStatus('saving');

    try {
      await galleryService.reorderVideos(galleryId, reordered.map(v => v.id));
      setSaveStatus('saved');
    } catch (err) {
      setSaveStatus('error');
      setError('Failed to reorder videos');
      // Revert on error
      setVideoLinks(videoLinks);
    }
  };

  // Invitation handlers
  const handleInvitationAdded = (invitation) => {
    setInvitations([...invitations, invitation]);
  };

  const handleInvitationRemoved = (email) => {
    setInvitations(invitations.filter(inv => inv.email !== email));
  };

  const handleInvitationUpdated = (email, canEdit) => {
    setInvitations(invitations.map(inv =>
      inv.email === email ? { ...inv, canEdit } : inv
    ));
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this gallery? This action cannot be undone.')) {
      return;
    }

    try {
      await galleryService.deleteGallery(galleryId);
      navigate('/dashboard');
    } catch (err) {
      setError('Failed to delete gallery. Please try again.');
    }
  };

  const handleRetry = () => {
    setSaveStatus('idle');
    setError(null);
  };

  // Selection handlers for bulk operations
  const handleToggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    setSelectedVideoIds(new Set());
  };

  const handleToggleVideoSelection = useCallback((videoId) => {
    setSelectedVideoIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(videoId)) {
        newSet.delete(videoId);
      } else {
        newSet.add(videoId);
      }
      return newSet;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedVideoIds(new Set(videoLinks.map(v => v.id)));
  }, [videoLinks]);

  const handleDeselectAll = useCallback(() => {
    setSelectedVideoIds(new Set());
  }, []);

  const handleBulkDelete = async () => {
    if (selectedVideoIds.size === 0) return;

    const count = selectedVideoIds.size;
    if (!window.confirm(`Are you sure you want to delete ${count} video${count > 1 ? 's' : ''}? This action cannot be undone.`)) {
      return;
    }

    setSaveStatus('saving');
    try {
      const result = await galleryService.bulkRemoveVideos(galleryId, Array.from(selectedVideoIds));

      // Remove deleted videos from local state
      setVideoLinks(videoLinks.filter(v => !result.deletedVideoIds.includes(v.id)));
      setSelectedVideoIds(new Set());
      setIsSelectionMode(false);

      if (result.failedCount > 0) {
        setError(`Deleted ${result.successCount} videos. ${result.failedCount} failed: ${result.errors.join(', ')}`);
      }
      setSaveStatus('saved');
    } catch (err) {
      setSaveStatus('error');
      setError('Failed to delete videos');
    }
  };

  const tabs = [
    { id: 'videos', label: 'Videos', icon: 'M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z' },
    { id: 'general', label: 'Settings', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex flex-col">
        <SiteHeader />
        <div className="p-8 max-w-6xl mx-auto w-full">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading gallery...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !gallery) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex flex-col">
        <SiteHeader />
        <div className="p-8 max-w-6xl mx-auto w-full">
          <Breadcrumb items={[
            { label: 'My galleries', href: '/dashboard' },
            { label: 'Gallery' }
          ]} />
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mt-4">
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex flex-col">
      <SiteHeader />
      <div className="p-8 max-w-6xl mx-auto w-full">
        <Breadcrumb items={[
          { label: 'My galleries', href: '/dashboard' },
          { label: gallery?.gallery?.name || 'Untitled Gallery', href: `/gallery/${gallery?.gallery?.urlName}` }
        ]} />

        {/* Back Button */}
        <div className="mt-4">
          <BackButton to={`/gallery/${gallery?.gallery?.urlName}`} label="Back to gallery" />
        </div>

        {/* Header with Save Status */}
        <div className="mt-6 mb-6 bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={handleNameBlur}
                className="text-3xl font-bold text-gray-900 w-full border-0 border-b-2 border-transparent hover:border-gray-300 focus:border-primary-500 focus:outline-none px-0 py-1 transition-colors bg-transparent"
                placeholder="Gallery name"
              />
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onBlur={handleDescriptionBlur}
                rows="2"
                className="text-gray-600 mt-2 w-full border-0 border-b-2 border-transparent hover:border-gray-300 focus:border-primary-500 focus:outline-none px-0 py-1 transition-colors resize-none bg-transparent"
                placeholder="Add a description..."
              />
            </div>
            <div className="flex items-center gap-4 flex-shrink-0">
              <SaveStatusIndicator status={saveStatus} onRetry={handleRetry} />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-4 border-b-2 font-medium text-sm transition-colors ${activeTab === tab.id
                      ? 'border-primary-500 text-primary-500'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
                  </svg>
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Videos Tab */}
            {activeTab === 'videos' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Manage Videos</h2>
                    <p className="text-sm text-gray-600 mt-1">
                      {videoLinks.length} {videoLinks.length === 1 ? 'video' : 'videos'} in this gallery
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {videoLinks.length > 0 && (
                      <button
                        onClick={handleToggleSelectionMode}
                        className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors ${
                          isSelectionMode
                            ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                        </svg>
                        {isSelectionMode ? 'Cancel' : 'Select'}
                      </button>
                    )}
                    <button
                      onClick={() => setShowAddVideoModal(true)}
                      className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 font-medium flex items-center gap-2 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add Video
                    </button>
                  </div>
                </div>

                {/* Selection Controls Bar */}
                {isSelectionMode && videoLinks.length > 0 && (
                  <div className="mb-4 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-between">
                    <span className="text-sm text-gray-600">
                      {selectedVideoIds.size} of {videoLinks.length} selected
                    </span>
                    <div className="flex items-center gap-4">
                      <div className="flex gap-2">
                        <button
                          onClick={handleSelectAll}
                          className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                        >
                          Select All
                        </button>
                        <span className="text-gray-300">|</span>
                        <button
                          onClick={handleDeselectAll}
                          className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                        >
                          Deselect All
                        </button>
                      </div>
                      {selectedVideoIds.size > 0 && (
                        <button
                          onClick={handleBulkDelete}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium flex items-center gap-2 transition-colors"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Delete ({selectedVideoIds.size})
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {videoLinks.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No videos yet</h3>
                    <p className="text-gray-600 mb-4">Add your first video to get started</p>
                    <button
                      onClick={() => setShowAddVideoModal(true)}
                      className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 font-medium"
                    >
                      Add Your First Video
                    </button>
                  </div>
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={videoLinks.map((v) => v.id || v.url)}
                      strategy={rectSortingStrategy}
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {videoLinks.map((video) => (
                          <SortableVideoItem
                            key={video.id || video.url}
                            video={video}
                            onUpdate={handleUpdateVideo}
                            onDelete={handleDeleteVideo}
                            isSelectable={isSelectionMode}
                            isSelected={selectedVideoIds.has(video.id)}
                            onToggleSelect={handleToggleVideoSelection}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}
              </div>
            )}

            {/* General Tab */}
            {activeTab === 'general' && (
              <div className="space-y-8">
                {/* Privacy & Access Section */}
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Privacy & Access</h2>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <label className="flex items-start cursor-pointer">
                      <input
                        type="radio"
                        checked={!isPrivate}
                        onChange={() => handlePrivacyChange(false)}
                        className="mt-1 w-4 h-4 text-primary-500 border-gray-300 focus:ring-primary-500"
                      />
                      <span className="ml-3">
                        <span className="font-medium text-gray-900 block">Anyone with link</span>
                        <span className="text-sm text-gray-600">
                          Anyone who has the link can view this gallery
                        </span>
                      </span>
                    </label>

                    <label className="flex items-start cursor-pointer">
                      <input
                        type="radio"
                        checked={isPrivate}
                        onChange={() => handlePrivacyChange(true)}
                        className="mt-1 w-4 h-4 text-primary-500 border-gray-300 focus:ring-primary-500"
                      />
                      <span className="ml-3">
                        <span className="font-medium text-gray-900 block">Invite only</span>
                        <span className="text-sm text-gray-600">
                          Only people you invite can access this gallery
                        </span>
                      </span>
                    </label>
                  </div>

                  {isPrivate && (
                    <div className="mt-6">
                      <div className="mb-4">
                        <h3 className="text-base font-semibold text-gray-900 mb-2">Invited Users</h3>
                        <p className="text-sm text-gray-600 mb-4">
                          Add email addresses of people you want to share this gallery with
                        </p>
                      </div>

                      <InvitationInput
                        galleryId={parseInt(galleryId)}
                        invitations={invitations}
                        onInvitationAdded={handleInvitationAdded}
                        onInvitationRemoved={handleInvitationRemoved}
                        onInvitationUpdated={handleInvitationUpdated}
                        disabled={false}
                      />
                    </div>
                  )}
                </div>

                {/* Video Settings Section */}
                <div className="border-t pt-8">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Video Settings</h2>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="flex items-start cursor-pointer">
                      <input
                        type="checkbox"
                        checked={addNewVideosAtBeginning}
                        onChange={(e) => handleAddNewVideosAtBeginningChange(e.target.checked)}
                        className="mt-1 w-4 h-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <span className="ml-3">
                        <span className="font-medium text-gray-900 block">Add new videos at the beginning</span>
                        <span className="text-sm text-gray-600">
                          New videos will appear at the start of the gallery
                        </span>
                      </span>
                    </label>
                  </div>
                </div>

                {/* Import from YouTube Channel Section */}
                <ImportFromChannelSection
                  galleryId={parseInt(galleryId)}
                  onImportComplete={(importedVideos) => {
                    // Add imported videos to the list
                    if (addNewVideosAtBeginning) {
                      setVideoLinks([...importedVideos, ...videoLinks]);
                    } else {
                      setVideoLinks([...videoLinks, ...importedVideos]);
                    }
                    setSaveStatus('saved');
                  }}
                />

                {/* Danger Zone Section - Collapsible */}
                <div className="border-t pt-8">
                  <button
                    onClick={() => setIsDangerZoneExpanded(!isDangerZoneExpanded)}
                    className="w-full flex items-center justify-between text-left group"
                  >
                    <div className="flex items-center gap-3">
                      <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <h2 className="text-lg font-semibold text-red-900">Danger Zone</h2>
                    </div>
                    <svg
                      className={`w-5 h-5 text-gray-400 transition-transform ${isDangerZoneExpanded ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {isDangerZoneExpanded && (
                    <div className="mt-4 bg-red-50 border-2 border-red-200 rounded-lg p-6">
                      <div className="flex items-start gap-4">
                        <svg className="w-8 h-8 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-red-900 mb-2">Delete Gallery</h3>
                          <p className="text-red-800 mb-4">
                            Once you delete this gallery, there is no going back. This will permanently delete the gallery and all of its videos.
                          </p>
                          <button
                            onClick={handleDelete}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors flex items-center gap-2"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Delete This Gallery
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Video Modal */}
      <AddVideoModal
        isOpen={showAddVideoModal}
        onClose={() => setShowAddVideoModal(false)}
        onAdd={handleAddVideo}
      />
    </div>
  );
}

export default ManageGalleryPage;
